/////////////////////////////////////////////////////
//                                                   //
//  Program: SlaConfig.jsx                          //
//  Application: Property                           //
//  Option: Manage Property SLA Configuration        //
//                                                   //
/////////////////////////////////////////////////////
import React, { Component } from "react";
import PropertyConfigService from "../../services/PropertyConfigService";
import Alerts from "../common/Alerts";

const DEFAULT_TIERS = [
  { label: "Low", color: "#1E9E6A", offset_hours: 0.5 },
  { label: "Medium", color: "#E0A82E", offset_hours: 1 },
  { label: "High", color: "#D93838", offset_hours: 2 },
];

class SlaConfig extends Component {
  constructor(props) {
    super(props);
    this.state = {
      propertySlug: this.props.property_slug,
      loading: false,
      isSubmit: false,
      showAlertModal: false,
      alertModalType: "",
      alertModalTitle: "",
      alertModalMessage: "",
      sla_enabled: true,
      tiers: DEFAULT_TIERS.map((tier) => ({ ...tier })),
    };
    this.changeHandler = this.changeHandler.bind(this);
    this.saveConfig = this.saveConfig.bind(this);
    this.resetToSaved = this.resetToSaved.bind(this);
    this.toggleSlaStatus = this.toggleSlaStatus.bind(this);
  }

  componentDidMount() {
    this.getConfig();
  }

  async getConfig() {
    if (this.state.propertySlug === undefined) {
      return;
    }
    this.setState({ loading: true });
    let res = await PropertyConfigService.getPropertyConfig(
      this.state.propertySlug
    );
    if (global.successStatus.includes(res.status)) {
      // See PropertyConfig::showConfig - sla_status is properties.sla_status (1 = on, 2 = off),
      // configuration is the saved tier list (empty whenever sla_status is off)
      let resData = res.data || {};
      let configuration = Array.isArray(resData.configuration) ? resData.configuration : [];

      this.setState({
        sla_enabled: Number(resData.sla_status) !== 2,
        // Stored on the backend as offset_minutes - converted to hours here since the UI edits in hours
        tiers:
          configuration.length === DEFAULT_TIERS.length
            ? configuration.map((tier, index) => ({
                label: tier.label || DEFAULT_TIERS[index].label,
                color: tier.color || DEFAULT_TIERS[index].color,
                offset_hours:
                  tier.offset_minutes != null
                    ? tier.offset_minutes / 60
                    : DEFAULT_TIERS[index].offset_hours,
              }))
            : DEFAULT_TIERS.map((tier) => ({ ...tier })),
      });
    }
    this.setState({ loading: false });
  }

  // Just flips the local switch - persisted together with the tiers when "Save Changes"
  // is clicked (see saveConfig), not via a separate API call.
  toggleSlaStatus() {
    this.setState({ sla_enabled: !this.state.sla_enabled });
  }

  changeHandler(event, index) {
    const { name, value } = event.target;
    const tiers = [...this.state.tiers];
    const field = name.startsWith("tier_") ? name.slice(5) : name;
    tiers[index] = { ...tiers[index], [field]: value };
    this.setState({ tiers });
  }

  resetToSaved() {
    this.getConfig();
  }

  saveConfig = async (e) => {
    e.preventDefault();
    this.setState({ loading: true, isSubmit: true });
    let inputData = {
      property_slug: this.state.propertySlug,
      sla_status: this.state.sla_enabled ? 1 : 2,
      // Backend still stores/computes SLA tiers in offset_minutes - convert the hours the UI collects back to minutes
      configuration: this.state.tiers.map((tier) => ({
        label: tier.label,
        offset_minutes: Math.round(parseFloat(tier.offset_hours) * 60) || 1,
        color: tier.color,
      })),
    };

    let res = await PropertyConfigService.savePropertyConfig(inputData);
    if (global.successStatus.includes(res.status)) {
      this.setState({
        showAlertModal: true,
        alertModalType: "success",
        alertModalTitle: "Success",
        alertModalMessage: res.message ? res.message : "SLA configuration saved successfully.",
      });
      this.getConfig();
    } else {
      this.setState({
        showAlertModal: true,
        alertModalType: "error",
        alertModalTitle: res.data && res.data.message ? res.data.message : "Error!",
        alertModalMessage:
          res.data && res.data.message ? res.data.message : "Failed to save SLA configuration.",
      });
    }
    this.setState({ loading: false, isSubmit: false });
    setTimeout(
      () => this.setState({ showAlertModal: false }),
      global.alert_close_time
    );
  };

  render() {
    const { tiers } = this.state;

    return (
      <div>
        <main className="p-0">
          <div className="col-md-12 pl-0 pr-0">
            <Alerts
              show={this.state.showAlertModal}
              type={this.state.alertModalType}
              title={this.state.alertModalTitle}
              message={this.state.alertModalMessage}
            />
          </div>
          <div className="dashboardRightcard m-0" style={{ padding: "20px 30px" }}>
            <div className="d-flex justify-content-between align-items-start flex-wrap mg-b-15">
              <div>
                <h4 className="mg-b-5" style={{ fontFamily: "Oswald-Regular" }}>SLA Configuration</h4>
              </div>
              <div className="d-flex align-items-center">
                <span
                  className="mg-r-15"
                  style={{
                    color: this.state.sla_enabled ? "#1E9E6A" : "#9ca3af",
                    fontWeight: 600,
                    fontSize: 13,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: this.state.sla_enabled ? "#1E9E6A" : "#9ca3af",
                      marginRight: 6,
                    }}
                  ></span>
                  {this.state.sla_enabled ? "Active" : "Inactive"}
                </span>
                <label className="form-label mg-b-0 mg-r-10">SLA tracking</label>
                <div
                  className={
                    this.state.sla_enabled
                      ? "az-toggle on hand-cursor"
                      : "az-toggle hand-cursor"
                  }
                  onClick={this.toggleSlaStatus}
                >
                  <span></span>
                </div>
              </div>
            </div>

            <div
              style={{
                fontSize: 11,
                letterSpacing: 1,
                color: "#6b7280",
                fontWeight: 600,
                marginBottom: 12,
              }}
            >
              ESCALATION TIERS
            </div>
            <div>
              <div
                className="d-flex"
                style={{ fontSize: 11, letterSpacing: 1, color: "#6b7280", paddingBottom: 10 }}
              >
                <div style={{ width: "28%", fontWeight: 600, paddingRight: 16 }}>STATUS LABEL</div>
                <div style={{ width: "32%", fontWeight: 600, paddingRight: 16 }}>COLOR</div>
                <div style={{ flex: 1, fontWeight: 600 }}>APPLIES</div>
              </div>
              {tiers.map((tier, index) => (
                <div
                  key={index}
                  className="sla-tier-row"
                  style={{ padding: "14px 0", borderTop: "1px solid #eee" }}
                >
                  <div style={{ width: "28%", paddingRight: 16 }} className="sla-tier-row">
                    <span
                      style={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        background: tier.color,
                        marginRight: 8,
                        flex: "0 0 auto",
                      }}
                    ></span>
                    <input
                      type="text"
                      name="tier_label"
                      className="form-control d-inline-block"
                      style={{ width: 150, border: "none", borderBottom: "1px solid #ddd", fontWeight: 600 }}
                      value={tier.label}
                      onChange={(e) => this.changeHandler(e, index)}
                      maxLength="50"
                    />
                  </div>
                  <div style={{ width: "32%", paddingRight: 16 }}>
                    <input
                      type="color"
                      name="tier_color"
                      value={tier.color}
                      onChange={(e) => this.changeHandler(e, index)}
                      style={{ width: 34, height: 34, border: "none", padding: 0, background: "none" }}
                    />
                  </div>
                  <div style={{ flex: 1 }} className="sla-tier-row">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      name="tier_offset_hours"
                      className="form-control sla-minutes-input"
                      style={{width: "32%", marginRight: 8 }}
                      value={tier.offset_hours ?? ""}
                      onChange={(e) => this.changeHandler(e, index)}
                    />
                    <span style={{ fontSize: 13, color: "#6b7280" }}>
                      Hrs &nbsp;{index === 0 ? "Up to" : "After"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex justify-content-end align-items-center mg-t-20">
              <div className="d-flex align-items-center" style={{ whiteSpace: "nowrap" }}>
                <button
                  type="button"
                  className="gf-btn-lg-outline mg-r-7"
                  onClick={this.resetToSaved}
                  disabled={this.state.loading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="gf-btn-lg-stroke"
                  onClick={this.saveConfig}
                  disabled={this.state.isSubmit}
                >
                  {this.state.isSubmit ? global.loader : "Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }
}

export default SlaConfig;
